import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AccessRemovedDialogComponent } from '@components/access-removed-dialog/access-removed-dialog.component';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { RequestAccessDialogComponent } from '@components/request-access-dialog/request-access-dialog.component';
import { AccessRequestSocketService } from '@service/access-request-socket.service';
import { AccessRequestService } from '@service/access-request.service';
import { AuthService } from '@service/auth.service';
import { NotificationsService } from '@service/homepage-notifications.service';
import { VesselOwnershipService } from '@service/vessel-ownership.service';
import { VesselSocketService } from '@service/vessel-socket.service';
import { VesselService } from '@service/vessel.service';
import { AccessRequest, AccessRequestStatus } from 'icreate/common/models/access-request';
import { ProjectStatus, Vessel } from 'icreate/common/models/vessel';
import { lastValueFrom, Subject, take, takeUntil } from 'rxjs';

@Component({
    selector: 'app-locked-vessel-notification',
    templateUrl: './locked-vessel-notification.component.html',
    styleUrl: './locked-vessel-notification.component.css',
    standalone: false,
})
export class LockedVesselNotificationComponent implements OnChanges, OnInit, OnDestroy {
    @Input() vessel: Vessel;
    @Input() pageName: string;

    @ViewChild('claimOwnershipConfirmationDialog')
    claimOwnershipConfirmationDialog: ConfirmationWindowComponent;

    accessRequested: boolean = false;
    currentUser: { name: string; oid: string; email: string };
    destroySubscriptions$: Subject<void> = new Subject();
    ownerName: string;

    constructor(
        private dialog: MatDialog,
        private accessRequestService: AccessRequestService,
        private accessRequestSocketService: AccessRequestSocketService,
        private authService: AuthService,
        private vesselSocketService: VesselSocketService,
        private notificationsService: NotificationsService,
        private ownershipService: VesselOwnershipService,
        private vesselService: VesselService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.currentUser = this.authService.getCurrentUser();
        this.startSubscriptions();
    }

    private startSubscriptions() {
        this.accessRequestSocketService
            .requestCreated$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async (accessRequest: AccessRequest) => {
                if (accessRequest.vesselId !== this.vessel.id) return;
                this.vessel.VesselAccessRequests.push(accessRequest);
                if (this.currentUser.oid == accessRequest.userOid) {
                    this.accessRequested = true;
                }
            });

        this.accessRequestSocketService
            .startedEditing$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async (accessRequest: AccessRequest) => {
                if (accessRequest.vesselId !== this.vessel.id) return;
                if (this.vessel?.VesselAccessRequests?.length > 0) {
                    this.vessel.VesselAccessRequests.forEach((request, i) => {
                        if (accessRequest.id === request.id) {
                            this.vessel.VesselAccessRequests[i] = accessRequest;
                        }
                    });
                }
            });
        this.accessRequestSocketService
            .finishedEditing$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async (accessRequest: AccessRequest) => {
                if (accessRequest.vesselId !== this.vessel.id) return;
                if (this.vessel?.VesselAccessRequests?.length > 0) {
                    this.vessel.VesselAccessRequests.forEach((request, i) => {
                        if (accessRequest.id === request.id) {
                            if (this.currentUser.oid == accessRequest.userOid) {
                                this.accessRequested = false;
                            }
                            this.vessel.VesselAccessRequests[i] = accessRequest;
                        }
                    });
                }
            });

        this.accessRequestSocketService
            .requestUpdated$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async (accessRequest: AccessRequest) => {
                if (accessRequest.vesselId !== this.vessel.id) return;
                if (this.vessel?.VesselAccessRequests?.length > 0) {
                    this.vessel.VesselAccessRequests.forEach((request, i) => {
                        if (accessRequest.id === request.id) {
                            if (
                                (accessRequest.requestStatus == AccessRequestStatus.Rejected ||
                                    accessRequest.requestStatus == AccessRequestStatus.Removed ||
                                    accessRequest.requestStatus == AccessRequestStatus.Accepted) &&
                                this.currentUser.oid == accessRequest.userOid
                            ) {
                                this.accessRequested = false;
                            }
                            this.vessel.VesselAccessRequests[i] = accessRequest;
                        }
                    });
                }
            });

        this.accessRequestSocketService
            .requestCanceled$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe((canceledRequests: AccessRequest[]) => {
                if (canceledRequests.length === 0) return;
                if (canceledRequests[0].vesselId !== this.vessel.id) return;

                if (this.vessel?.VesselAccessRequests?.length > 0) {
                    const canceledIds = new Set(canceledRequests.map(r => r.id));

                    const isCurrentUserAffected = canceledRequests.some(r => r.userOid === this.currentUser.oid);

                    if (isCurrentUserAffected) {
                        this.accessRequested = false;
                    }

                    this.vessel.VesselAccessRequests = this.vessel.VesselAccessRequests.filter(
                        request => !canceledIds.has(request.id)
                    );
                }
            });

        this.vesselSocketService
            .vesselUpdated$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe((updatedVessel: Vessel) => {
                if (this.vessel.id === updatedVessel.id) {
                    this.vessel = updatedVessel;
                }
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.vessel) return;
        this.currentUser = this.authService.getCurrentUser();
        if (this.vessel.projectStatus === ProjectStatus.Editing) {
            if (this.currentUser.oid === this.vessel.ownerOid) return;
            else if (this.vessel?.VesselAccessRequests?.length >= 0) {
                this.accessRequested = this.vessel.VesselAccessRequests.some(
                    request =>
                        request.userOid === this.currentUser.oid &&
                        !request.messageRead &&
                        request.requestStatus === AccessRequestStatus.Pending
                );
            }
        }
    }

    ngOnDestroy(): void {
        this.destroySubscriptions$.next();
        this.destroySubscriptions$.complete();
    }

    get showRequestAccess(): boolean {
        if (!this.vessel || this.accessRequested) return false;
        if (!this.vessel.ownerOid) return false;
        if (this.vessel.projectStatus !== ProjectStatus.Editing) return false;
        if (this.isCurrentUserOwner) return false;
        if (!this.vessel.VesselAccessRequests) return true;

        return !this.vessel.VesselAccessRequests.some(
            request =>
                request.userOid === this.currentUser.oid &&
                !request.messageRead &&
                (request.requestStatus === AccessRequestStatus.Rejected ||
                    request.requestStatus === AccessRequestStatus.Editing ||
                    request.requestStatus === AccessRequestStatus.Removed ||
                    request.requestStatus === AccessRequestStatus.Leave ||
                    request.requestStatus === AccessRequestStatus.Accepted)
        );
    }

    get showAccessRejected(): boolean {
        if (!this.vessel || this.accessRequested || this.showAccessRemoved) return false;
        if (!this.vessel.ownerOid) return false;
        if (this.vessel.projectStatus !== ProjectStatus.Editing) return false;
        if (this.isCurrentUserOwner) return false;
        if (!this.vessel.VesselAccessRequests) return false;

        return this.vessel.VesselAccessRequests?.some(
            request =>
                request.userOid === this.currentUser.oid &&
                !request.messageRead &&
                request.requestStatus === AccessRequestStatus.Rejected
        );
    }

    get showAccessAccepted(): boolean {
        if (!this.vessel) return false;
        if (!this.vessel.ownerOid) return false;
        if (this.vessel.projectStatus !== ProjectStatus.Editing) return false;
        if (this.isCurrentUserOwner) return false;
        if (!this.vessel.VesselAccessRequests) return false;

        return this.vessel.VesselAccessRequests.some(
            request =>
                request.userOid === this.currentUser.oid &&
                !request.messageRead &&
                request.requestStatus === AccessRequestStatus.Accepted
        );
    }

    get showAccessRemoved(): boolean {
        if (!this.vessel) return false;
        if (!this.vessel.ownerOid) return false;
        if (this.vessel.projectStatus !== ProjectStatus.Editing) return false;
        if (this.isCurrentUserOwner) return false;
        if (!this.vessel.VesselAccessRequests) return false;

        return this.vessel.VesselAccessRequests.some(
            request =>
                request.userOid === this.currentUser.oid &&
                !request.messageRead &&
                request.requestStatus === AccessRequestStatus.Removed
        );
    }

    get showAccessRequested(): boolean {
        return !!this.vessel?.ownerOid && this.accessRequested;
    }

    get isCurrentUserOwner(): boolean {
        return this.currentUser?.oid === this.vessel?.ownerOid;
    }

    get showVesselInReview(): boolean {
        return !!this.vessel?.ownerOid && this.vessel.projectStatus === ProjectStatus.PendingApproval;
    }

    get showVesselApproved(): boolean {
        return !!this.vessel?.ownerOid && this.vessel.projectStatus === ProjectStatus.Approved;
    }

    get isVesselUnassigned(): boolean {
        return (
            !!this.vessel &&
            !this.vessel.ownerOid &&
            !this.vessel.ownerName &&
            this.vessel.projectStatus !== ProjectStatus.undefined
        );
    }

    get vesselNeedsNewVersion(): boolean {
        return (
            !!this.vessel &&
            !this.vessel.ownerOid &&
            !this.vessel.ownerName &&
            this.vessel.projectStatus === ProjectStatus.undefined
        );
    }

    async requestVesselAccess() {
        const dialogRef = this.dialog.open(RequestAccessDialogComponent, { width: '474px' });
        const requestAccess = await lastValueFrom(dialogRef.afterClosed());
        if (requestAccess) {
            const currentUser = this.authService.getCurrentUser();
            try {
                await this.accessRequestService.requestAccess(
                    currentUser.name,
                    currentUser.oid,
                    this.vessel.id,
                    this.vessel.projectVersion
                );
                this.accessRequested = true;
            } catch (err) {
                console.error(err);
            }
        }
    }

    async startEdit() {
        try {
            const accessRequest = this.vessel.VesselAccessRequests.find(
                request =>
                    request.userOid === this.currentUser.oid &&
                    !request.messageRead &&
                    request.requestStatus === AccessRequestStatus.Accepted
            );
            this.accessRequested = false;
            await this.accessRequestService.startEdit(accessRequest.id);
            this.accessRequestService.setCurrentAccessRequestId(accessRequest.id);
            const updatedVessel = await this.vesselService.getVessel(this.vessel.id);
            await this.vesselService.updateVesselInList(updatedVessel);
        } catch (err) {
            console.error(err);
        }
    }

    async showAccessRemovedDialog() {
        this.dialog.open(AccessRemovedDialogComponent, {
            width: '474px',
            data: { vessel: this.vessel.name },
        });
        try {
            const notifications = await this.notificationsService.getAll(this.currentUser.oid);
            let requestId;
            if (this.vessel?.VesselAccessRequests?.length > 0) {
                this.vessel.VesselAccessRequests.forEach((request, i) => {
                    if (
                        request.requestStatus == AccessRequestStatus.Removed &&
                        this.currentUser.oid == request.userOid
                    ) {
                        request.messageRead = true;
                        requestId = request.id;
                        this.vessel.VesselAccessRequests[i] = request;
                    }
                });
            }
            if (requestId) {
                const notification = notifications.find(notification => notification.id == requestId);
                if (notification) {
                    await this.notificationsService.updateAccessRequest(notification.id, true, true);
                }
            }
        } catch (err) {
            console.error(err);
        }
    }

    async closeRejectedMessage() {
        try {
            const rejectedIndex = this.vessel.VesselAccessRequests?.findIndex(
                request =>
                    request.userOid === this.currentUser.oid &&
                    !request.messageRead &&
                    request.requestStatus === AccessRequestStatus.Rejected
            );
            if (rejectedIndex < 0) return;
            const rejectedNotification = this.vessel.VesselAccessRequests[rejectedIndex];
            await this.notificationsService.updateAccessRequest(rejectedNotification.id, true, true);

            this.vessel.VesselAccessRequests[rejectedIndex].messageRead = true;
        } catch (err) {
            console.error(err);
        }
    }

    async claimOwnership() {
        this.claimOwnershipConfirmationDialog.setVisible(true);
        let confirmationOption = await lastValueFrom(
            this.claimOwnershipConfirmationDialog.onOptionSelected.pipe(take(1))
        );

        if (confirmationOption === ConfirmationWindowTypes.SAVE && this.currentUser) {
            try {
                if (this.currentUser?.oid) {
                    await this.ownershipService.claimVesselOwnership({
                        vesselId: this.vessel.id,
                        vesselVersion: this.vessel.projectVersion,
                        userName: this.currentUser.name ?? null,
                        userOid: this.currentUser.oid ?? null,
                    });
                    const updatedVessel = await this.vesselService.getVessel(this.vessel.id);
                    await this.vesselService.updateVesselInList(updatedVessel);
                }
            } catch (error) {
                console.error(error);
            }
        }
    }

    goBackToVessel() {
        this.router.navigateByUrl(`home/${this.vessel.shipyardId}/${this.vessel.id}`);
    }
}
